import { forwardRef, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { BarChart } from "senf-atomic-design-system";
import type { Project } from "senf-shared/types";
import { getAlpha } from "senf-shared/utils/helpers";
import { useDimensions } from "senf-shared/utils/hooks";
import { generateThemeColor, hex2hsl } from "../../../styles/helpers";
import PieChart from "../../core/charts/base/PieChart";
import { Legend } from "../../core/charts/Legend";
import type { Result } from "../survey/legacy/survey.types";

export interface ParticipationStructureProps {
  showToggles?: boolean;
  data?: Result[];
  project?: Project;
}

export function processDemographicsData(data: Result[]) {
  const byGender = {
    female: 0,
    male: 0,
    diverse: 0,
    preferNotToSay: 0,
  };
  const ageGroups = {
    "<18": { ...byGender },
    "18-24": { ...byGender },
    "25-34": { ...byGender },
    "35-44": { ...byGender },
    "45-54": { ...byGender },
    "55-64": { ...byGender },
    "65-74": { ...byGender },
    "75+": { ...byGender },
  };

  data.reduce((acc, user) => {
    const { age: ageString, gender } = user.userData || user;
    const age = Number(ageString);
    if (gender) byGender[gender]++;
    if (!age) return acc;

    if (age < 18) {
      acc["<18"][gender]++;
    } else if (age >= 18 && age <= 24) {
      acc["18-24"][gender]++;
    } else if (age >= 25 && age <= 34) {
      acc["25-34"][gender]++;
    } else if (age >= 35 && age <= 44) {
      acc["35-44"][gender]++;
    } else if (age >= 45 && age <= 54) {
      acc["45-54"][gender]++;
    } else if (age >= 55 && age <= 64) {
      acc["55-64"][gender]++;
    } else if (age >= 65 && age <= 74) {
      acc["65-74"][gender]++;
    } else {
      acc["75+"][gender]++;
    }

    return acc;
  }, ageGroups);
  const byAge = Object.entries(ageGroups).map(([age, group]) => ({
    ...group,
    age,
  }));
  return {
    byAge,
    byGender,
  };
}

const ParticipationStructure = forwardRef<
  HTMLDivElement,
  ParticipationStructureProps
>(({ showToggles, data: resultData, project }, ref) => {
  const { t } = useTranslation();
  const [data, setData] = useState<Record<string, string | number>[] | null>(
    null,
  );
  const [piechartData, setPiechartData] = useState(null);
  const [barChartData, setBarChartData] = useState(null);

  const { baseIndex } = generateThemeColor(
    hex2hsl(project?.color || "#ffd061"),
  );
  const base = Math.min(Math.max(baseIndex, 3), 8);

  const dataKeys = {
    female: {
      key: "female",
      stroke: `var(--primary-${(base + 1) * 100})`,
    },
    male: {
      key: "male",
      stroke: `var(--primary-${base * 100})`,
    },
    diverse: {
      key: "diverse",
      stroke: `var(--primary-${(base - 1) * 100})`,
    },
    preferNotToSay: {
      key: "preferNotToSay",
      stroke: "var(--surface-depressed)",
    },
  };

  function processData(data) {
    setData(processDemographicsData(data).byAge);
  }

  useEffect(() => {
    if (resultData) {
      processData(resultData);
    }
  }, [resultData]);

  useEffect(() => {
    const filteredData =
      data?.map((d) => {
        return Object.keys(dataKeys).reduce(
          (obj, key) => ({
            ...obj,
            [key]: typeof d[key] === "number" ? d[key] : 0,
            name: d.age,
            label: d.age,
          }),
          {} as Record<string, string | number>,
        );
      }) || [];

    setBarChartData(filteredData);

    const averageAges =
      resultData?.reduce(
        (acc, curr) => {
          const { gender, age } = curr.userData || curr;
          if (!gender || !age) return acc;
          acc[gender] ??= [];
          acc[gender].push(age);
          return acc;
        },
        {} as Record<string, number[]>,
      ) || {};

    if (filteredData.length) {
      let totalSum = 0;
      const piechartDataPre = Object.keys(dataKeys).map((key) => {
        const sum = filteredData.reduce((acc, obj) => +obj[key] + acc, 0);
        const averageAge = averageAges[key]
          ? averageAges[key].reduce((acc, age) => +age + acc, 0) /
            averageAges[key].length
          : 0;
        totalSum += sum;
        return {
          key,
          name: key,
          label: t(key),
          fill: dataKeys[key]?.stroke,
          stroke: dataKeys[key]?.stroke,
          value: sum,
          averageAge,
        };
      });

      const piechartData = piechartDataPre.map((obj) => {
        const percentage = `${((obj.value / totalSum) * 100).toFixed(0)}%`;

        return {
          ...obj,
          valueLabel: percentage,
          sublabel: `⌀ ${obj.averageAge} ${t("global:age")}`,
        };
      });
      setPiechartData(piechartData);
    }
  }, [data]);

  const [container, dimensions] = useDimensions();

  const breakpoint = useMemo(() => dimensions.width < 640, [dimensions.width]);
  const legend = [
    {
      value: "female",
      label: "female",
      color: dataKeys.female.stroke,
    },
    {
      value: "male",
      label: "male",
      color: dataKeys.male.stroke,
    },
    {
      value: "diverse",
      label: "diverse",
      color: dataKeys.diverse.stroke,
    },
    {
      value: "preferNotToSay",
      label: "preferNotToSay",
      color: dataKeys.preferNotToSay.stroke,
    },
  ];
  const legendPayload = useMemo(
    () =>
      legend?.map((item, index) => ({
        ...item,
        label: t(item.label),
        legendReference: breakpoint ? getAlpha(index) : undefined,
      })),
    [breakpoint],
  );

  return (
    <div
      className="flex h-full flex-col justify-between mt-1 relative z-10"
      ref={ref}
    >
      <div
        className="flex h-[200px] w-full max-w-full items-center z-50 relative"
        ref={container}
      >
        {!resultData?.length ? (
          <div className="bg-surface-subdued flex h-full w-full items-center justify-center rounded-xl">
            <span className="body-md text-text-subdued">
              {t("util:nothingToShowYet", {
                val: t("global:data"),
              })}
            </span>
          </div>
        ) : (
          <>
            {barChartData && (
              <div className="h-full w-1/2 z-50 relative">
                <BarChart
                  data={barChartData}
                  stacked
                  bars={legend}
                  className="p-0"
                  minHeight={0}
                />
              </div>
            )}
            {piechartData && (
              <div className="w-1/2">
                <PieChart
                  data={piechartData.map((item) => ({
                    ...item,
                    label: item.label,
                    percent:
                      Number(
                        item.valueLabel.replace("%", "").replace(",", "."),
                      ) / 100,
                    value: Number(item.value),
                  }))}
                  label
                  legendReference={breakpoint}
                  innerRadius={breakpoint ? 17.5 : 30}
                  outerRadius={breakpoint ? 35 : 50}
                  labelSize={breakpoint ? "sm" : "md"}
                  width={dimensions.width / 2}
                  isAnimationActive={false}
                  minHeight={0}
                  legend={false}
                  pathLength={{
                    r: breakpoint ? 15 : 20,
                    x: breakpoint ? 5 : 15,
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
      <Legend payload={legendPayload} className="justify-start mx-0 -mb-1" />
    </div>
  );
});

ParticipationStructure.displayName = "ParticipationStructure";

export default ParticipationStructure;
